import type { StorageLocation } from "@storytell/cp-client";
import { createImagesClient } from "@storytell/cp-client";
import { createEffect, createSignal, onCleanup } from "solid-js";
import { imageUrlCache } from "~/lib/imageUrlCache";

interface UseSignedImageURLOptions {
  ttl?: string;
  authToken?: string;
}

interface UseSignedImageURLResult {
  url: () => string | undefined;
  loading: () => boolean;
  error: () => Error | undefined;
  refetch: () => void;
}

/**
 * Hook to fetch and cache signed image URLs.
 * Checks cache before making API calls and stores results for future use.
 */
export function useSignedImageURL(
  location: () => StorageLocation,
  options: () => UseSignedImageURLOptions = () => ({}),
): UseSignedImageURLResult {
  const [url, setUrl] = createSignal<string | undefined>();
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<Error | undefined>();
  const [refetchTrigger, setRefetchTrigger] = createSignal(0);

  createEffect(() => {
    let cancelled = false;
    const currentLocation = location();
    const opts = options();
    const _trigger = refetchTrigger();

    const fetchURL = async () => {
      const cachedUrl = imageUrlCache.get(currentLocation);
      if (cachedUrl) {
        if (!cancelled) {
          setUrl(cachedUrl);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        const imagesClient = createImagesClient();
        const results = await imagesClient.getSignedURLs([currentLocation], opts.ttl, opts.authToken);

        if (cancelled) {
          return;
        }

        if (results.length === 0) {
          throw new Error("No signed URL returned from server");
        }

        const result = results[0];
        if (!result) {
          throw new Error("No signed URL returned from server");
        }

        if (result.url) {
          setUrl(result.url);

          if (result.expiresAt) {
            const expiresAt = new Date(result.expiresAt);
            imageUrlCache.set(currentLocation, result.url, expiresAt);
          }
        }

        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    fetchURL();

    onCleanup(() => {
      cancelled = true;
    });
  });

  const refetch = () => {
    setRefetchTrigger((c) => c + 1);
  };

  return {
    url,
    loading,
    error,
    refetch,
  };
}
